import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Text, VStack, useDisclosure, useToast, useTheme } from '@chakra-ui/react';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { brandApi } from '../../../services/api/brand';
import { ff } from '../../../theme';
import {
  CreatePackageModal,
  LocalProfileSection,
  CreatorLinkSection,
  TopVideosSection,
  BrandsWorkedWithSection,
  TurnaroundTimeSection,
  PackagesSection,
  WelcomeLocalCreatorsModal,
  type LocalCreatorsBrandChip,
  type LocalCreatorsPackageRow,
} from './localCreators';
import { useMarketplaceProfileForm } from './localCreators/hooks/useMarketplaceProfileForm';
import UploadContentModal from './UploadContentModal';
import DeactivateAccountModal from './localCreators/components/modals/DeactivateAccountModal';
import ProfilePhotoCropModal from './localCreators/components/modals/ProfilePhotoCropModal';

interface LocalCreatorsEditProps {
  onBack: () => void;
}

const LocalCreatorsEdit: React.FC<LocalCreatorsEditProps> = ({ onBack: _onBack }) => {
  const theme = useTheme();
  const { influencerData } = useContext(InfluencerDataContext);
  const toast = useToast();
  const form = useMarketplaceProfileForm(influencerData, toast);
  const { isWelcomePending, markWelcomeShown } = form;

  const { isOpen: isWhereOpen, onToggle: onToggleWhere } = useDisclosure();
  const {
    isOpen: isCreatePackageOpen,
    onOpen: onOpenCreatePackage,
    onClose: onCloseCreatePackage,
  } = useDisclosure();
  const {
    isOpen: isWelcomeLocalOpen,
    onOpen: onOpenWelcomeLocal,
    onClose: onCloseWelcomeLocal,
  } = useDisclosure();
  const {
    isOpen: isProfileCropOpen,
    onOpen: onOpenProfileCrop,
    onClose: onCloseProfileCrop,
  } = useDisclosure();

  const [editingPackage, setEditingPackage] = useState<LocalCreatorsPackageRow | null>(null);
  const [profileCropImageSrc, setProfileCropImageSrc] = useState<string | null>(null);
  const [isTopContentModalOpen, setIsTopContentModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [brandIcons, setBrandIcons] = useState<Record<string, string>>({});
  const [isLoadingBrandIcons, setIsLoadingBrandIcons] = useState(false);
  const pendingTopSlotIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isWelcomePending) {
      return;
    }
    markWelcomeShown();
    onOpenWelcomeLocal();
  }, [isWelcomePending, markWelcomeShown, onOpenWelcomeLocal]);

  useEffect(() => {
    return () => {
      if (profileCropImageSrc) {
        URL.revokeObjectURL(profileCropImageSrc);
      }
    };
  }, [profileCropImageSrc]);

  useEffect(() => {
    const fetchBrandIcons = async () => {
      if (form.marketplaceBrands.length === 0) {
        setBrandIcons({});
        return;
      }
      setIsLoadingBrandIcons(true);
      try {
        const icons = await brandApi.getBrandIcons(form.marketplaceBrands);
        const iconMap: Record<string, string> = {};
        icons.forEach((b) => {
          iconMap[b.name] = b.icon;
        });
        setBrandIcons(iconMap);
      } catch (error) {
        console.error('Error fetching brand icons:', error);
      } finally {
        setIsLoadingBrandIcons(false);
      }
    };
    void fetchBrandIcons();
  }, [form.marketplaceBrands]);

  const brandChips: LocalCreatorsBrandChip[] = useMemo(
    () =>
      form.marketplaceBrands.map((name) => ({
        id: name,
        display: name,
        logoUrl: brandIcons[name] ?? null,
      })),
    [form.marketplaceBrands, brandIcons]
  );

  const openCreatePackage = useCallback(() => {
    setEditingPackage(null);
    onOpenCreatePackage();
  }, [onOpenCreatePackage]);

  const openEditPackage = useCallback(
    (pkg: LocalCreatorsPackageRow) => {
      setEditingPackage(pkg);
      onOpenCreatePackage();
    },
    [onOpenCreatePackage]
  );

  const closePackageModal = useCallback(() => {
    setEditingPackage(null);
    onCloseCreatePackage();
  }, [onCloseCreatePackage]);

  const closeProfileCropModal = useCallback(() => {
    setProfileCropImageSrc(null);
    onCloseProfileCrop();
  }, [onCloseProfileCrop]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(form.publicUrl);
    toast({
      title: 'Link copied',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  }, [form.publicUrl, toast]);

  const openTopSlotPicker = (index: number) => {
    if (index < 0 || index >= form.topSlots.length) return;
    pendingTopSlotIndexRef.current = index;
    setIsTopContentModalOpen(true);
  };

  const pendingSlot =
    pendingTopSlotIndexRef.current !== null
      ? form.topSlots[pendingTopSlotIndexRef.current]
      : null;

  const handlePickProfileImageFile = useCallback(
    (file: File) => {
      if (!influencerData?.id) {
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Use an image file',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const nextUrl = URL.createObjectURL(file);
      setProfileCropImageSrc((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return nextUrl;
      });
      onOpenProfileCrop();
    },
    [influencerData?.id, toast, onOpenProfileCrop]
  );

  return (
    <Box
      fontFamily={theme.fonts.body}
      sx={{
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
      }}
    >
      <VStack spacing={8} align="stretch" pb={8}>
        <LocalProfileSection
          influencer={influencerData}
          displayAvatarUrl={form.profileImage?.url}
          bio={form.bio}
          onBioChange={form.setBio}
          onOpenAboutLocalCreators={onOpenWelcomeLocal}
          onSaveBio={form.saveBio}
          isSavingBio={form.isSavingBio}
          hasMarketplaceProfileImage={form.hasCustomProfileImage}
          onSelectProfileImageFile={handlePickProfileImageFile}
          isUploadingProfileImage={form.isUploadingProfileImage}
          onClearMarketplaceProfileImage={form.clearMarketplaceProfileImage}
        />
        <CreatorLinkSection
          slug={form.slug}
          onSlugChange={form.setSlug}
          onMarkSlugTouched={form.markSlugTouched}
          slugInputLooksLikePlaceholder={!form.slug}
          onCopy={handleCopy}
          isWhereOpen={isWhereOpen}
          onToggleWhere={onToggleWhere}
          onSaveCreatorLink={form.saveCreatorLink}
          isSavingCreatorLink={form.isSavingCreatorLink}
        />
        <TopVideosSection
          topSlots={form.topSlots}
          filledCount={form.filledCount}
          onSlotClick={openTopSlotPicker}
          onReorder={form.handleReorderTopSlots}
        />
        <BrandsWorkedWithSection
          brands={brandChips}
          isProfileLoading={form.isProfileLoading}
          isLoadingBrandIcons={isLoadingBrandIcons}
          onAddBrand={form.handleAddMarketplaceBrand}
          onRemoveBrand={form.handleRemoveBrand}
        />
        <TurnaroundTimeSection value={form.turnaround} onChange={form.saveTurnaround} />
        <PackagesSection
          packages={form.packages}
          onRemovePackage={(id) => {
            form.savePackages(form.packages.filter((x) => x.id !== id));
          }}
          onReorder={form.handleReorderPackages}
          onNewPackage={openCreatePackage}
          onEditPackage={openEditPackage}
        />
        <Box as="section" pt={6} mt={2} borderTop="1px solid" borderColor={ff.line}>
          <Text textStyle="eyebrow" color={ff.text2} mb={3}>
            Account
          </Text>
          <Text fontSize="15px" color={ff.body} lineHeight="1.5" mb={4}>
            {form.isAccountActive
              ? 'Your public creator page is visible to brands. You can turn it off anytime.'
              : 'Your page is hidden from brands. Turn it on when you want to receive orders.'}
          </Text>
          <Button
            type="button"
            w="100%"
            variant={form.isAccountActive ? 'inkOutline' : 'lime'}
            {...(form.isAccountActive ? { color: ff.danger, borderColor: ff.border } : {})}
            onClick={() => {
              if (form.isAccountActive) {
                setIsDeactivateModalOpen(true);
                return;
              }
              void form.activateAccount();
            }}
          >
            {form.isAccountActive ? 'Deactivate my account' : 'Activate my account'}
          </Button>
        </Box>
      </VStack>
      <CreatePackageModal
        isOpen={isCreatePackageOpen}
        onClose={closePackageModal}
        editingPackage={editingPackage}
        onSave={(row) => {
          const idx = form.packages.findIndex((p) => p.id === row.id);
          const next =
            idx >= 0 ? form.packages.map((p) => (p.id === row.id ? row : p)) : [...form.packages, row];
          form.savePackages(next);
        }}
      />
      <UploadContentModal
        isOpen={isTopContentModalOpen}
        onClose={() => {
          setIsTopContentModalOpen(false);
          pendingTopSlotIndexRef.current = null;
        }}
        uploadMode="marketplaceTop"
        onMarketplaceTopUpload={async (file, brandName, caption) => {
          const idx = pendingTopSlotIndexRef.current;
          if (idx === null) return;
          await form.handleMarketplaceTopUpload(file, brandName, caption, idx);
        }}
        onMarketplaceTopMetaUpdate={async (brandName, caption) => {
          const idx = pendingTopSlotIndexRef.current;
          if (idx === null) return;
          await form.handleMarketplaceTopMetaUpdate(brandName, caption, idx);
        }}
        initialBrand={pendingSlot?.associatedBrand}
        initialCaption={pendingSlot?.caption}
        initialContentUrl={pendingSlot?.url}
        initialIsVideo={pendingSlot?.isVideo}
        title={pendingSlot ? 'Edit content' : 'Add top content'}
      />
      <DeactivateAccountModal
        isOpen={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        onConfirm={async (reason) => {
          await form.deactivateAccount(reason);
          setIsDeactivateModalOpen(false);
        }}
      />
      <WelcomeLocalCreatorsModal isOpen={isWelcomeLocalOpen} onClose={onCloseWelcomeLocal} />
      <ProfilePhotoCropModal
        isOpen={isProfileCropOpen}
        imageSrc={profileCropImageSrc}
        onClose={closeProfileCropModal}
        onCompleteCroppedImage={form.uploadProfilePhoto}
      />
    </Box>
  );
};

export default LocalCreatorsEdit;

export type { LocalCreatorsBrandChip, LocalCreatorsPackageRow } from './localCreators';
